import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Music, Trophy, Clock, Shield, Phone, Mail, CheckCircle2, MapPin, Users, 
  Car, Star, ArrowRight, Calendar, Ticket, PartyPopper, GraduationCap,
  Wine, Sparkles, Timer, BadgeCheck
} from "lucide-react";
import ContactPopup from "../../components/ContactPopup";
import PageTransition from "../../components/PageTransition";

const SpecialEventsPage = () => {
  // Event venues
  const venues = [
    {
      name: "Suncorp Stadium",
      location: "Milton, Brisbane",
      capacity: "52,500",
      events: "NRL, Rugby Union, Soccer, Concerts",
      image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=400&h=250&fit=crop",
      tips: "Book pickup 30 mins after final whistle to avoid the rush"
    },
    {
      name: "The Gabba",
      location: "Woolloongabba, Brisbane",
      events: "Cricket, AFL, Concerts",
      capacity: "42,000",
      image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=400&h=250&fit=crop",
      tips: "We meet you at designated pickup zones away from crowds"
    },
    {
      name: "Brisbane Entertainment Centre",
      location: "Boondall",
      events: "Concerts, Comedy, Family Shows",
      capacity: "13,500",
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=250&fit=crop",
      tips: "Perfect for concert nights - skip the parking chaos"
    },
    {
      name: "QPAC & South Bank",
      location: "South Brisbane",
      events: "Theatre, Opera, Ballet, Symphony",
      capacity: "Various",
      image: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=400&h=250&fit=crop",
      tips: "Elegant arrival for cultural events"
    }
  ];

  // Event types
  const eventTypes = [
    {
      icon: Trophy,
      title: "Sporting Events",
      description: "NRL, AFL, Cricket, Rugby Union - arrive in style and leave without the parking hassle",
      examples: "Broncos, Lions, Heat, Reds matches"
    },
    {
      icon: Music,
      title: "Concerts & Festivals",
      description: "From stadium tours to intimate venues, we get you there and back safely",
      examples: "Suncorp Stadium concerts, Riverstage, Fortitude Music Hall"
    },
    {
      icon: GraduationCap,
      title: "School Formals & Graduations",
      description: "Make their special night unforgettable with a luxury arrival",
      examples: "Year 12 Formals, University Graduations"
    },
    {
      icon: Wine,
      title: "Dining & Nightlife",
      description: "Fine dining transfers, casino nights, and safe rides home",
      examples: "Treasury Casino, Howard Smith Wharves, Eagle Street"
    },
    {
      icon: PartyPopper,
      title: "Birthdays & Celebrations",
      description: "Milestone birthdays, anniversaries, and special occasions",
      examples: "21st birthdays, engagement parties, reunions"
    },
    {
      icon: Sparkles,
      title: "Race Days",
      description: "Arrive at the track in style - perfect for Melbourne Cup or local meets",
      examples: "Eagle Farm, Doomben, Gold Coast Turf Club"
    }
  ];

  // Hourly rates
  const hourlyRates = [
    { vehicle: "Executive Sedan", rate: "$85/hour", minHours: "2 hours", passengers: "Up to 3" },
    { vehicle: "Luxury SUV", rate: "$95/hour", minHours: "2 hours", passengers: "Up to 4" },
    { vehicle: "Premium Van", rate: "$120/hour", minHours: "3 hours", passengers: "Up to 7" },
    { vehicle: "Stretch Limo", rate: "$180/hour", minHours: "3 hours", passengers: "Up to 10" },
  ];

  // FAQ items
  const faqItems = [
    {
      question: "How does hourly hire work for events?",
      answer: "For events, we recommend hourly hire which includes pickup, waiting time during the event (driver stays nearby), and return transport. Minimum booking is typically 2-3 hours depending on the vehicle."
    },
    {
      question: "Can you wait during a 3-hour concert?",
      answer: "Absolutely! Your chauffeur will drop you at the venue entrance, park nearby, and be ready to collect you the moment you exit. No surge pricing, no hunting for rideshares in a crowd of thousands."
    },
    {
      question: "What about group bookings for corporate events?",
      answer: "We specialize in corporate event transport. Whether it's 4 executives or 40 staff members, we can coordinate multiple vehicles with a single point of contact. Monthly invoicing available for corporate accounts."
    },
    {
      question: "Do you provide service for school formals?",
      answer: "Yes! We're a popular choice for Year 12 formals. Our professional chauffeurs ensure a safe, memorable experience. We offer special formal packages including red carpet arrival and photo opportunities."
    },
    {
      question: "How far in advance should I book for major events?",
      answer: "For major sporting events and concerts at Suncorp Stadium or The Gabba, we recommend booking at least 1 week in advance. For school formals (especially October-November), book 2-3 months ahead."
    }
  ];

  // Why choose us for events
  const benefits = [
    {
      icon: Timer,
      title: "No Surge Pricing",
      description: "Unlike rideshare apps that triple prices after events, our rates are fixed."
    },
    {
      icon: BadgeCheck,
      title: "Guaranteed Pickup",
      description: "Your driver is waiting for YOU - no competing with 50,000 other fans."
    },
    {
      icon: Shield,
      title: "Safe Night Out",
      description: "Enjoy the event without worrying about driving or parking."
    },
    {
      icon: Users,
      title: "Group Friendly",
      description: "Vehicles for 1-11 passengers. Keep your group together."
    }
  ];

  // Schema.org data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Special Event & Concert Transfers Brisbane",
    "description": "Premium chauffeur service for sporting events, concerts, formals, and special occasions in Brisbane. Suncorp Stadium, The Gabba, and venue transfers.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Limo Near Me",
      "telephone": "+61481437772",
      "email": "info@limonearme.com.au"
    },
    "areaServed": {
      "@type": "City",
      "name": "Brisbane"
    },
    "serviceType": "Event Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Special Event Transfers Brisbane | Suncorp Stadium & Gabba | Concert Limo | Limo Near Me</title>
          <meta name="description" content="Premium chauffeur service for Brisbane events. Suncorp Stadium, The Gabba, concerts, formals & special occasions. No surge pricing. Group bookings available. Book your event transfer!" />
          <meta name="keywords" content="Suncorp Stadium transfer, Gabba chauffeur, Brisbane concert limo, event transfer Brisbane, formal car hire, NRL transfer, AFL transfer Brisbane, concert transport" />
          <link rel="canonical" href="https://www.limonearme.com.au/services/special-events" />
          <meta property="og:title" content="Special Event & Concert Transfers Brisbane | Limo Near Me" />
          <meta property="og:description" content="Premium transfers for Suncorp Stadium, The Gabba, concerts & special events. No surge pricing after events!" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/services/airport-transfers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Airport</Link>
                <Link to="/services/special-events" className="text-sm text-foreground font-medium">Events</Link>
                <Link to="/corporate-travel" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Corporate</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-purple-50 to-background">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Ticket className="h-4 w-4" />
                  No Surge Pricing After Events
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                  Special Event & Concert Transfers Brisbane
                </h1>
                <p className="text-xl text-muted-foreground mb-4">
                  <strong>Suncorp Stadium • The Gabba • Brisbane Entertainment Centre</strong>
                </p>
                <p className="text-muted-foreground mb-8">
                  Don't let transport stress ruin your event. While everyone else fights for rideshares with 3x surge pricing, your chauffeur is waiting at the designated pickup zone. Fixed rates, guaranteed pickup, and a luxury ride home.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/book">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Calendar className="h-5 w-5 mr-2" />
                      Book Event Transfer
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Get Group Quote
                    </Button>
                  </Link>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&h=400&fit=crop" 
                  alt="Luxury chauffeur service for Brisbane sporting events and concerts" 
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Trophy className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Game Day Ready</p>
                      <p className="text-sm text-muted-foreground">Skip the parking chaos</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose Us for Events */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Why Smart Event-Goers Choose Chauffeur</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                After 50,000 fans pour out of Suncorp Stadium, rideshare apps show "No cars available" or 4x surge pricing. Your chauffeur? Already waiting.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 border border-purple-100 text-center"
                >
                  <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-7 w-7 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Major Venues */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Brisbane's Major Event Venues</h2>
              <p className="text-muted-foreground">
                We provide premium transfers to all major Brisbane entertainment venues
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {venues.map((venue, index) => (
                <motion.div
                  key={venue.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden border border-black/5 hover:shadow-lg transition-all"
                >
                  <img 
                    src={venue.image} 
                    alt={`${venue.name} chauffeur transfer service`}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-xl">{venue.name}</h3>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        {venue.capacity} capacity
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> {venue.location}
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      <strong>Events:</strong> {venue.events}
                    </p>
                    <div className="bg-amber-50 rounded-lg p-3 text-sm">
                      <strong className="text-amber-800">Pro Tip:</strong>{" "}
                      <span className="text-amber-700">{venue.tips}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Event Types */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Events We Service</h2>
              <p className="text-muted-foreground">
                From the Broncos to black-tie galas, we've got you covered
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {eventTypes.map((event, index) => (
                <motion.div
                  key={event.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <event.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{event.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{event.description}</p>
                  <p className="text-xs text-primary font-medium">{event.examples}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Hourly Rates */}
        <section className="py-16 bg-purple-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Event Hourly Hire Rates</h2>
              <p className="text-purple-200">
                For events, we recommend hourly hire - includes pickup, wait time, and return
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {hourlyRates.map((rate, index) => (
                <motion.div
                  key={rate.vehicle}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-purple-800/50 rounded-xl p-6 text-center border border-purple-700"
                >
                  <h3 className="font-bold text-lg mb-2">{rate.vehicle}</h3>
                  <p className="text-3xl font-bold text-white mb-2">{rate.rate}</p>
                  <p className="text-sm text-purple-300 mb-1">Min: {rate.minHours}</p>
                  <p className="text-sm text-purple-300">{rate.passengers}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-purple-300 text-sm mb-4">
                All rates include GST. Tolls and parking fees included. No surge pricing.
              </p>
              <Link to="/book">
                <Button size="lg" className="bg-white text-purple-900 hover:bg-purple-50">
                  Get Exact Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Everything you need to know about event transfers
              </p>
            </div>

            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6"
                >
                  <h3 className="font-semibold text-lg mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Trophy className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Book Your Event Transfer</h2>
            <p className="text-primary-foreground/80 mb-8 text-lg">
              Don't let transport ruin your event. Book now and enjoy the occasion stress-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" className="w-full sm:w-auto px-8 bg-white text-primary hover:bg-gray-100">
                  Book Now
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto px-8">
                  Get Group Quote
                </Button>
              </Link>
            </div>
            <p className="mt-8 text-sm text-primary-foreground/70">
              Questions? Call us at{" "}
              <a href="tel:+61481437772" className="underline font-semibold">+61 481 437 772</a>
            </p>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default SpecialEventsPage;
